import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs';
import ToastMessage from '../../../../../common/ToastMessage';
import CustomDataGrid from '../../../../../common/CustomDataGrid';
import useRFP from '../../../../../store/ReactQueries/procurements/requestForProposal/useRFP';
import useDeleteRFP from '../../../../../store/ReactQueries/procurements/requestForProposal/useDeleteRFP';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../../utils/constants';
import { FieldErrors } from '../../../../../domain/helpers/Errors';
import { useLanguageStore } from '../../../../../store/useLanguageStore';
import { ReactComponent as PDFLogo } from '../../../../../images/pdf-icon.svg';
import { getRFPStatusString, RFPStatus } from '../../../../../enums/RFPStatus';
import { RFPInterface } from '../../../../../models/ProcurementModule/RequestForProposal';

export const RFP = () => {
    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<RFPInterface>({
        id: '',
        code: '',
        dueDate: '',
        budget: 1,
        description: '',
        vendors: [],
        criteriasApplied: [],
        isUrgent: false,
        status: RFPStatus.Open,
    });
    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });
    let { data: requestForProposals, error: requestForProposalsError, isLoading } = useRFP(page, pageSize);
    const deleteRFP = useDeleteRFP(page, pageSize);

    useEffect(() => {
        if (requestForProposalsError) ToastMessage.Failed(`Error: ${requestForProposalsError.message}`);
    }, [requestForProposalsError]);

    const handleDownloadPDF = async (id: string, documentUrl: string) => {
        if (id && documentUrl) {
            try {
                const link = document.createElement("a");
                link.href = documentUrl;
                link.download = "document.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

            } catch (error) {
                console.error("Error downloading PDF:", error);
            }
        }
    };

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'code', headerName: 'Code', type: "string", editable: false, width: 120 },
        { field: 'description', headerName: 'Description', type: "string", editable: false, width: 200 },
        {
            field: "dueDate",
            headerName: "DueDate",
            type: "string",
            editable: false,
            width: 100,
            renderCell: ({ row }) => (
                <span>
                    {
                        row.duedate ?? row.dueDate
                            ? dayjs(row.duedate ?? row.dueDate).format("DD/MM/YYYY")
                            : undefined
                    }
                </span>
            ),
        },
        { field: 'budget', headerName: 'Budget', type: "number", editable: false, width: 120 },
        {
            field: 'status',
            headerName: 'Status',
            type: "string",
            editable: false,
            width: 150,
            renderCell: ({ row }) => (
                <span>
                    {getRFPStatusString(row.status)}
                </span>
            )
        },
        {
            field: 'vendors',
            headerName: 'Vendors',
            type: "string",
            editable: false,
            width: 300,
            renderCell: ({ row }) => <div className="d-flex align-items-center justify-content-between">
                <div className="text-truncate" style={{ maxWidth: 300 }}>
                    {row.vendors && row.vendors.length > 0
                        ? row.vendors.map((item: any, index: number) => {
                            const length = row.vendors.length - 1;
                            return <span key={index}>
                                {item?.vendor?.name}
                                {index < length && ', '}
                            </span>

                        }
                        )
                        : ''}
                </div>
            </div>
        },
        {
            field: 'evaluationCriteria',
            headerName: 'CriteriasApplied',
            type: "string",
            editable: false,
            width: 250,
            renderCell: ({ row }) => {
                const evaluationCriterias = row && row.evaluationCriteria ? row.evaluationCriteria.map((r: any) => r.rfpEvaluationCriteria.title).join(',') : '';
                return <div className="d-flex align-items-center justify-content-between">
                    <div className="text-truncate" style={{ maxWidth: 250 }}>
                        <span title={evaluationCriterias}>{evaluationCriterias}</span>
                    </div>
                </div>
            }
        },
        { field: 'isUrgent', headerName: 'Urgent', type: "boolean", editable: false, width: 80 },
        {
            field: 'documentUrl',
            headerName: 'DocumentTemplate',
            type: "string",
            editable: false,
            width: 80,
            renderCell: ({ row }) => {
                return row.documentUrl
                    ? <div className="d-flex align-items-center justify-content-between">
                        <div
                            className='pdf-icon-download'
                            role='button'
                            title={'Download template'}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDownloadPDF(row.id, row.documentUrl)
                            }}
                        >
                            <PDFLogo width="30" height="30" />
                        </div>
                    </div>
                    : null
            }
        },
    ];

    const addItems = () => {
        setForm({ ...form, id: '', description: '', code: '' });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();
    };

    const handleDelete = async (id: string, confirmation: boolean = true) => {
        const proceed = confirmation
            ? await ToastMessage.Confirm(language['ToastDeleteItem'])
            : true;

        if (proceed) {
            deleteRFP.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    const showColumnModal = ['code', 'description', 'dueDate', 'isUrgent'];
    const hideColumns = ['id'];

    return <div className='d-flex flex-row transition-wrapper'>
        <div className="content">
            <CustomDataGrid
                name={language['RequestForProposals']}
                newAddComponent="RequestForProposals"
                rows={requestForProposals || []}
                form={form}
                page={page}
                modal={modal}
                errors={errors}
                columns={columns}
                loading={isLoading}
                pageSize={pageSize}
                hideColumns={hideColumns}
                showColumnModal={showColumnModal}
                setPage={setPage}
                setForm={setForm}
                setModal={setModal}
                addItems={addItems}
                setErrors={setErrors}
                setPageSize={setPageSize}
                handleDelete={handleDelete}
                handleSave={function (): Promise<void> {
                    throw new Error('Function not implemented.');
                }} />
        </div>
    </div>
}
export default RFP;